import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Clock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { k: "mon", label: "Mon" },
  { k: "tue", label: "Tue" },
  { k: "wed", label: "Wed" },
  { k: "thu", label: "Thu" },
  { k: "fri", label: "Fri" },
  { k: "sat", label: "Sat" },
  { k: "sun", label: "Sun" },
];

/**
 * Delivery Hours card — buyer self-service business-hours window.
 * Outside the window, the routing engine rejects matching leads BEFORE
 * touching the buyer's wallet. Same component used on /dashboard/billing
 * and /dashboard/campaign for consistency.
 */
export default function DeliveryHoursCard({ initial, onSaved }) {
  const [form, setForm] = useState(() => normalise(initial));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setForm(normalise(initial)); setDirty(false); }, [JSON.stringify(initial || {})]);

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const toggleDay = (k) => {
    setForm((f) => {
      const has = f.days.includes(k);
      return { ...f, days: has ? f.days.filter((d) => d !== k) : [...f.days, k] };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/client/preferences", { delivery_hours: form });
      toast.success(form.enabled ? "Business hours updated — outside this window leads will be rejected." : "Business hours disabled — leads route 24/7.");
      setDirty(false);
      onSaved?.(form);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save business hours");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="delivery-hours-card" className="border-slate-200">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-900">Business hours</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-md leading-snug">
              Only route leads to me during these hours. Outside the window leads are rejected before your wallet is touched — no waste, no after-hours costs.
            </p>
          </div>
          <Switch
            checked={form.enabled}
            onCheckedChange={(v) => update({ enabled: v })}
            data-testid="delivery-hours-toggle"
          />
        </div>

        <div className={`space-y-4 transition-opacity ${form.enabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
          <div>
            <div className="text-xs font-medium text-slate-600 mb-2">Active days</div>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = form.days.includes(d.k);
                return (
                  <button
                    key={d.k}
                    type="button"
                    onClick={() => toggleDay(d.k)}
                    data-testid={`delivery-day-${d.k}`}
                    className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                      on
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {on && <Check className="inline size-3 mr-1 -mt-0.5" />}
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-slate-600 mb-1">Start</span>
              <Input
                type="time"
                value={form.start}
                onChange={(e) => update({ start: e.target.value })}
                data-testid="delivery-start"
                className="h-9"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-600 mb-1">End</span>
              <Input
                type="time"
                value={form.end}
                onChange={(e) => update({ end: e.target.value })}
                data-testid="delivery-end"
                className="h-9"
              />
            </label>
          </div>

          <div className="text-[11px] text-slate-400 font-mono">
            Timezone: {form.timezone} · all leads timestamped UTC, window evaluated in your local time.
          </div>
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
          <span className="text-xs text-slate-500">
            {form.enabled
              ? `${(form.days.length === 7 ? "Every day" : `${form.days.length} day${form.days.length === 1 ? "" : "s"}`)} · ${form.start}–${form.end}`
              : "Always-on (24/7 delivery)"}
          </span>
          <Button
            size="sm"
            disabled={!dirty || saving}
            onClick={save}
            data-testid="delivery-hours-save"
            className="bg-slate-900 hover:bg-slate-800"
          >
            {saving ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function normalise(initial) {
  const d = initial || {};
  return {
    enabled:  !!d.enabled,
    timezone: d.timezone || "Europe/London",
    days:     Array.isArray(d.days) && d.days.length ? d.days : ["mon","tue","wed","thu","fri"],
    start:    d.start || "09:00",
    end:      d.end   || "18:00",
  };
}
